import {
  getWikiControllerGetWikiStatusQueryKey,
  type GetWikiStatusResponseOutput,
  type SuccessResponseOutput,
  useWikiControllerGetWikiDocsByWorkspace,
  useWikiControllerGetWikiStatus,
  useWikiControllerPostActionDocs,
  useWikiControllerPostWorkspaceAsWiki,
  type wikiControllerPostWorkspaceAsWiki,
  type WikiControllerPostWorkspaceAsWikiParams,
} from '@docnboard/rest';
import { type UseMutationOptions, useQueryClient } from '@tanstack/react-query';

const WIKI_QUERY_KEY = ['wiki', 'workspaces'] as const;

export function getWikiQueryKey(workspaceId?: string, docId?: string) {
  // Все документы
  if (!workspaceId) return WIKI_QUERY_KEY;

  // Все документы в рабочем пространстве
  if (!docId) return [...WIKI_QUERY_KEY, workspaceId, 'docs'] as const;

  // Один документ
  return [...WIKI_QUERY_KEY, workspaceId, 'docs', docId] as const;
}

export const usePostActionDocs = useWikiControllerPostActionDocs;

export const useGetWikiDocsByWorkspace =
  useWikiControllerGetWikiDocsByWorkspace;

export const useGetWikiStatus = useWikiControllerGetWikiStatus;

export const useUpdateWikiStatus = <
  TError = SuccessResponseOutput,
  TContext = unknown,
>(
  workspaceId: string,
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof wikiControllerPostWorkspaceAsWiki>>,
      TError,
      { params: WikiControllerPostWorkspaceAsWikiParams },
      TContext
    >;
  }
) => {
  const queryClient = useQueryClient();

  const queryKey = getWikiControllerGetWikiStatusQueryKey({ workspaceId });

  return useWikiControllerPostWorkspaceAsWiki({
    mutation: {
      onMutate: async variables => {
        await queryClient.cancelQueries({ queryKey });

        const previousData =
          queryClient.getQueryData<GetWikiStatusResponseOutput>(queryKey);

        queryClient.setQueryData<GetWikiStatusResponseOutput>(
          queryKey,
          oldData => {
            if (!oldData) return oldData;
            return {
              ...oldData,
              data: {
                ...oldData.data,
                enable: String(variables.params.enable) === 'true',
              },
            };
          }
        );

        return { previousData };
      },
      onError: (_err, _variables, context) => {
        if (context?.previousData) {
          queryClient.setQueryData(queryKey, context.previousData);
        }
      },
      onSettled: async () => {
        await queryClient.invalidateQueries({ queryKey });
      },
    },
    ...options?.mutation,
  });
};
